import React from 'react';
import PropTypes from 'prop-types';

import cn from 'classnames';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha
} from 'react-google-recaptcha-v3';

import api from '../../js/api-helper';
import deepClone from '@creuna/utils/deep-clone';
import isEqual from '@creuna/utils/is-equal';

import Button from '../button';
import Formsy from 'formsy-react';
import './formsy-with-validation';

const formPropTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  className: PropTypes.string,
  endpoint: PropTypes.string.isRequired,
  fieldNameMapping: PropTypes.object,
  id: PropTypes.string,
  onBeforeSubmit: PropTypes.func,
  onChange: PropTypes.func,
  onInvalidSubmit: PropTypes.func,
  onResponse: PropTypes.func,
  onRef: PropTypes.func,
  onValidate: PropTypes.func,
  onValidationResponse: PropTypes.func,
  recaptchaSiteKey: PropTypes.string,
  showSubmitButton: PropTypes.bool,
  submitLabel: PropTypes.string,
  validationEndpoint: PropTypes.string
};

const formDefaultProps = {
  onBeforeSubmit: () => {},
  onChange: () => {},
  onInvalidSubmit: () => {},
  onResponse: () => {},
  onRef: () => {},
  onValidate: () => {},
  onValidationResponse: () => {},
  showSubmitButton: true
};

class InnerForm extends React.Component {
  static propTypes = {
    ...formPropTypes,
    executeRecaptcha: PropTypes.func
  };

  static propTypesMeta = 'exclude';

  static defaultProps = formDefaultProps;

  state = {
    isLoading: false,
    isValidating: false
  };

  formData = {};

  reverseMapFieldNames = responseObject => {
    if (!this.props.fieldNameMapping) {
      return responseObject;
    }

    const reverseMappedResponse = {};
    Object.entries(responseObject).forEach(([key, value]) => {
      const originalName = Object.keys(this.props.fieldNameMapping).find(
        oldName => this.props.fieldNameMapping[oldName] === key
      );
      reverseMappedResponse[originalName || key] = value;
    });
    return reverseMappedResponse;
  };

  isValidationModel = responseObject => {
    if (!Object.keys(responseObject).length) {
      return false;
    }

    // Reverse map the response keys to match the form field names
    const mappedResponse = this.reverseMapFieldNames(responseObject);

    const model = this.formsy.getModel();
    return Object.keys(mappedResponse).every(
      key => Object.keys(model).indexOf(key) !== -1
    );
  };

  onChange = formData => {
    if (!isEqual(formData, this.formData)) {
      this.props.onChange(formData);
      this.formData = deepClone(formData);
    }
  };

  onValidSubmit = async (formData, resetForm, invalidateForm) => {
    if (this.state.isLoading) {
      return;
    }

    // Use formdata from onBeforeSubmit only if it returned data
    formData = this.props.onBeforeSubmit(formData)
      ? this.props.onBeforeSubmit(formData)
      : formData;

    // If fieldNameMapping is provided, map the field names in the data back to what the API expects before submitting
    if (this.props.fieldNameMapping) {
      const mappedData = { ...formData };
      Object.entries(this.props.fieldNameMapping).forEach(
        ([oldName, newName]) => {
          if (mappedData[oldName] !== undefined) {
            mappedData[newName] = mappedData[oldName];
            delete mappedData[oldName];
          }
        }
      );
      formData = mappedData;
    }

    if (this.props.recaptchaSiteKey && this.props.executeRecaptcha) {
      try {
        const token = await this.props.executeRecaptcha('submit_form');
        formData.recaptchaToken = token;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Recaptcha error', err);
        return;
      }
    }

    if (this.props.validationEndpoint) {
      this.setState({ isLoading: true }, () => {
        api
          .execute(this.props.validationEndpoint, formData)
          .then(response => {
            if (this.isValidationModel(response)) {
              // Map the validation keys back to the form field names before invalidating
              const validationErrors = this.reverseMapFieldNames(response);
              invalidateForm(validationErrors);
              this.props.onValidationResponse(false);
              this.setState({ isLoading: false });
            } else {
              this.props.onValidationResponse(true);
              this.doApiSubmit(formData);
            }
          })
          .catch(() => {
            this.setState({ isLoading: false });
            this.props.onResponse({});
          });
      });
    } else {
      this.doApiSubmit(formData);
    }
  };

  doApiSubmit = formData => {
    this.setState(
      {
        isLoading: true
      },
      () => {
        api
          .execute(this.props.endpoint, formData)
          .then(response => {
            this.setState({ isLoading: false });
            this.props.onResponse(response);
          })
          .catch(() => {
            this.setState({ isLoading: false });
            this.props.onResponse({});
          });
      }
    );
  };

  render() {
    return (
      <Formsy
        action={this.props.endpoint}
        className={cn('form', this.props.className)}
        id={this.props.id}
        method="post"
        onChange={this.onChange}
        onValid={() => this.props.onValidate(true)}
        onValidSubmit={this.onValidSubmit}
        onInvalid={() => this.props.onValidate(false)}
        onInvalidSubmit={this.props.onInvalidSubmit}
        ref={formsy => {
          this.formsy = formsy;
          this.props.onRef(formsy);
        }}
        tabIndex={-1}
        noValidate
      >
        {typeof this.props.children === 'function'
          ? this.props.children({ isLoading: this.state.isLoading })
          : this.props.children}
        {this.props.showSubmitButton && (
          <div className="form-nav">
            <Button
              className="form-submit"
              disabled={this.state.isLoading}
              text={this.props.submitLabel}
              type="submit"
            />
          </div>
        )}
      </Formsy>
    );
  }
}

// Functional component to use the reCAPTCHA hook
// eslint-disable-next-line react/no-multi-comp
const RecaptchaWrapper = props => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  return <InnerForm {...props} executeRecaptcha={executeRecaptcha} />;
};

// Main Form component that conditionally wraps with reCAPTCHA provider
// eslint-disable-next-line react/no-multi-comp
const Form = props => {
  if (props.recaptchaSiteKey) {
    return (
      <GoogleReCaptchaProvider reCaptchaKey={props.recaptchaSiteKey}>
        <RecaptchaWrapper {...props} />
      </GoogleReCaptchaProvider>
    );
  }

  return <InnerForm {...props} />;
};

Form.propTypes = formPropTypes;

Form.propTypesMeta = 'exclude';

Form.defaultProps = formDefaultProps;

export default Form;
